import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { CheckMark, CheckMarkContainer } from '../checkMark'

const Container = styled.div<{ $selected?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.5em;
    padding: 0.75em;
    width: 100%;
    aspect-ratio: 1;
    border: 1px solid ${(p) => p.theme.colors.lighterGrey};
    border-radius: 8px;
    background-color: ${(p) => p.theme.colors.primaryGrey};
    text-align: center;
    user-select: none;

    ${CheckMarkContainer} {
        position: absolute;
        right: 0.5em;
        top: 0.5em;
        display: ${(p) => (p.$selected ? 'block' : 'none')};
    }

    &:hover ${CheckMarkContainer} {
        display: block;
    }
`

const IconWrapper = styled.div`
    color: ${(p) => p.theme.colors.lightGrey};

    svg {
        width: 2.5em;
        height: 2.5em;
        display: block;
    }
`

const FileName = styled.span`
    font-size: 0.7em;
    color: ${(p) => p.theme.colors.darkGrey};
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`

const TypeLabel = styled.span`
    font-size: 0.6em;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.colors.lightGrey};
`

const DownloadLink = styled.a`
    font-size: 0.7em;
    font-weight: ${(p) => p.theme.fontWeight.semiBold};
    color: ${(p) => p.theme.colors.blue.link};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`

const DocumentIcon: FC = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
        <path
            d="M14 2v6h6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
        />
    </svg>
)

const VideoIcon: FC = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <rect
            x="3"
            y="5"
            width="18"
            height="14"
            rx="2"
            stroke="currentColor"
            strokeWidth="1.5"
        />
        <path d="m10 9 5 3-5 3V9Z" fill="currentColor" />
    </svg>
)

const AudioIcon: FC = () => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
    >
        <path
            d="M9 18V6l10-2v12"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <circle cx="6" cy="18" r="3" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="16" cy="16" r="3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
)

const iconForType = (type?: string) => {
    switch ((type ?? '').toUpperCase()) {
        case 'VIDEO':
            return <VideoIcon />
        case 'AUDIO':
            return <AudioIcon />
        // PDF / DOCUMENT / anything else -> generic document icon
        default:
            return <DocumentIcon />
    }
}

/**
 * Derive a human-friendly file name from an S3 storage key. Storage keys look
 * like `protected/{userId}/{consultId}/{uuid}` and often carry no extension, so
 * fall back to the attachment type when no meaningful basename is present.
 */
const deriveFileName = (storageKey?: string, type?: string): string => {
    const basename = (storageKey ?? '').split('/').filter(Boolean).pop() ?? ''
    if (basename && basename.includes('.')) return basename
    return basename || (type ? type.toLowerCase() : 'attachment')
}

interface Props {
    attachmentType?: string
    /** Presigned URL for the stored object (already re-signed upstream). */
    downloadUrl?: string
    contentStorageKey?: string
    selected: boolean
    onSelect: () => void
}

export const NonImageAttachment: FC<Props> = ({
    attachmentType,
    downloadUrl,
    contentStorageKey,
    selected,
    onSelect,
}) => {
    const fileName = useMemo(
        () => deriveFileName(contentStorageKey, attachmentType),
        [contentStorageKey, attachmentType]
    )

    return (
        <Container $selected={selected}>
            <IconWrapper>{iconForType(attachmentType)}</IconWrapper>

            <FileName title={fileName}>{fileName}</FileName>
            {attachmentType && <TypeLabel>{attachmentType}</TypeLabel>}

            {downloadUrl && (
                <DownloadLink
                    href={downloadUrl}
                    download={fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(event) => event.stopPropagation()}
                >
                    Download
                </DownloadLink>
            )}

            <CheckMark selected={selected} onClick={onSelect} />
        </Container>
    )
}
